import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { savePassword } from '../../../Login/AuthService';
import { useNavigate, useParams } from 'react-router-dom';

const validationSchema = yup.object({
  password: yup
    .string()
    .required('Password is required.')
    .min(6, 'Password must be at least 6 characters.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
    ),
  passwordConfirmation: yup
    .string()
    .required('Confirm Password is required.')
    .oneOf([yup.ref('password')], 'Passwords must match.'),
});

const Form = (): JSX.Element => {
  const { id, token } = useParams();
  const navigator = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const initialValues = {
    password: '',
    passwordConfirmation: '',
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const submissionValues = {
      ...values,
      id,
      token,
    };

    try {
      const response = await savePassword(submissionValues);
      if (response.status === 200) {
        navigator('/login');
        window.location.reload();
        console.log('Password reset successfully.');
      } else {
        console.log('Password reset failed.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Password reset failed. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{
            textTransform: 'uppercase',
            fontWeight: 'medium',
          }}
          gutterBottom
          color={'text.secondary'}
        >
          Recover account
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
          }}
        >
          Forgot your password?
        </Typography>
        <Typography color="text.secondary">
          Enter your new password below to reset your password.
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <TextField
              label="New Password *"
              variant="outlined"
              type="password"
              name="password"
              fullWidth
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                formik.touched.password && String(formik.errors.password)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Confirm Password *"
              variant="outlined"
              type="password"
              name="passwordConfirmation"
              fullWidth
              value={formik.values.passwordConfirmation}
              onChange={formik.handleChange}
              error={
                formik.touched.passwordConfirmation &&
                Boolean(formik.errors.passwordConfirmation)
              }
              helperText={
                formik.touched.passwordConfirmation &&
                String(formik.errors.passwordConfirmation)
              }
            />
            {errorMessage && (
              <Typography color="error" variant="body2" gutterBottom>
                {errorMessage}
              </Typography>
            )}
          </Grid>
          <Grid item container xs={12}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretched', sm: 'center' }}
              justifyContent={'space-between'}
              width={1}
              maxWidth={600}
              margin={'0 auto'}
            >
              <Box marginBottom={{ xs: 1, sm: 0 }}>
                <Button
                  size={'large'}
                  variant={'outlined'}
                  component={Link}
                  href={'/login'}
                  fullWidth
                >
                  Back to login
                </Button>
              </Box>
              <Button size={'large'} variant={'contained'} type={'submit'}>
                Reset Password
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default Form;
